import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Heart, Pencil, Trash2, Sparkles, Search, X, ArrowRight } from "lucide-react";
import api, { imageUrl, CATEGORIES, PATTERNS, COLOR_FAMILIES } from "@/lib/api";
import { saveResult } from "@/lib/session";
import { useAuth } from "@/context/AuthContext";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { prepareImage, PHOTO_ERROR_MSG } from "@/lib/image";
import { MoireSymbol } from "@/components/MoireLogo";
import { SCENE } from "@/lib/categoryAssets";
import UploadZone from "@/components/UploadZone";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import { Swatch } from "@/components/ColorSwatch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const GROUPS = ["all", "tops", "bottoms", "dresses", "outerwear", "shoes", "bags", "accessories"];
const STYLE_TAGS = ["Casual", "Smart Casual", "Work", "Formal", "Evening", "Sport", "Travel"];

export const matchOptionsFor = (category = "") => {
  const c = category.toLowerCase();
  if (c.includes("dress")) return ["Shoes", "Jacket", "Bag", "Jewellery", "Accessories", "Complete Look"];
  if (/(trouser|jean|skirt|short)/.test(c)) return ["Shirt / Top", "Shoes", "Jacket", "Accessories", "Complete Look"];
  if (/(shoe|sneaker|loafer|heel|boot)/.test(c)) return ["Trousers / Bottom", "Dress", "Top", "Complete Look"];
  if (/(jacket|blazer|coat)/.test(c)) return ["Shirt / Top", "Trousers / Bottom", "Shoes", "Complete Look"];
  if (/(bag|jewell|accessor)/.test(c)) return ["Complete Look"];
  return ["Trousers / Bottom", "Shoes", "Jacket", "Accessories", "Complete Look"];
};

export const toAnalysis = (it) => ({
  item: it.custom_name || it.item || it.category, category: it.category,
  primary_color_name: it.primary_color_name, primary_color_hex: it.primary_color_hex,
  primary_color_rgb: [0, 0, 0], secondary_colors: it.secondary_colors || [],
  color_family: it.color_family || "neutral", pattern: it.pattern || "solid",
  material: it.material || null, style: (it.style_tags || ["Smart Casual"])[0], confidence: 0.9,
});

export default function Wardrobe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [group, setGroup] = useState("all");
  const [query, setQuery] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    const params = {};
    if (group !== "all") params.category = group;
    if (query) params.q = query;
    if (favOnly) params.favourite = true;
    const [{ data }, s] = await Promise.all([
      api.get("/wardrobe", { params }),
      api.get("/wardrobe/stats").catch(() => ({ data: null })),
    ]);
    setItems(data);
    if (s?.data) setStats(s.data);
  }, [user, group, query, favOnly]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (user === false) return <SignInInvite navigate={navigate} />;
  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">My Wardrobe</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EA] mt-2">Everything you own. Styled intelligently.</h1>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-piece-btn"
          className="flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
          <Plus size={16} strokeWidth={2} /> Add a Piece
        </button>
      </div>

      {items && items.length === 0 && group === "all" && !query && !favOnly ? (
        <EmptyStart onAdd={() => setAddOpen(true)} />
      ) : (
        <>
          {stats && stats.total > 0 && <StatsBar stats={stats} />}

          <div className="flex flex-wrap items-center gap-2 mt-10">
            {GROUPS.map((g) => (
              <button key={g} onClick={() => setGroup(g)} data-testid={`wcat-${g}`}
                className={`px-4 py-2 font-sans text-[11px] tracking-luxe uppercase border transition-colors ${
                  group === g ? "border-[#C8A97E] bg-[#C8A97E] text-[#0D0D0D]" : "border-[#F5F2EA]/15 text-[#F5F2EA]/70 hover:border-[#C8A97E]"}`}>
                {g}
              </button>
            ))}
            <button onClick={() => setFavOnly((f) => !f)} data-testid="wfav-filter"
              className={`flex items-center gap-1.5 px-4 py-2 font-sans text-[11px] tracking-luxe uppercase border transition-colors ${
                favOnly ? "border-[#C8A97E] text-[#C8A97E]" : "border-[#F5F2EA]/15 text-[#F5F2EA]/70 hover:border-[#C8A97E]"}`}>
              <Heart size={13} strokeWidth={1.5} fill={favOnly ? "#C8A97E" : "none"} /> Favourites
            </button>
            <div className="relative ml-auto">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8170]" strokeWidth={1.5} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search my wardrobe" data-testid="wardrobe-search"
                className="bg-transparent border border-[#F5F2EA]/15 pl-9 pr-3 py-2 font-sans text-sm text-[#F5F2EA] placeholder:text-[#8A8170] focus:border-[#C8A97E] outline-none w-48" />
            </div>
          </div>

          {items && items.length === 0 ? (
            <EmptyCategory group={group} onAdd={() => setAddOpen(true)} />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
              {items?.map((it) => (
                <WardrobeTile key={it.id} item={it} onOpen={() => setDetail(it)} onChanged={load} />
              ))}
            </div>
          )}
        </>
      )}

      {addOpen && <AddDialog open={addOpen} onClose={() => setAddOpen(false)} onAdded={load} />}
      {detail && <DetailDialog item={detail} onClose={() => setDetail(null)} onChanged={load} navigate={navigate} />}
    </div>
  );
}

function SignInInvite({ navigate }) {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-5">
      <MoireSymbol size={52} />
      <h1 className="font-serif text-4xl md:text-6xl text-[#F5F2EA] mt-8">Your wardrobe.<br />Your style.</h1>
      <p className="font-sans text-sm text-[#8A8170] mt-6 max-w-md leading-relaxed">
        Create your private MOIRÉ wardrobe and discover more ways to wear what you already own.
      </p>
      <div className="flex gap-4 mt-10">
        <button onClick={() => navigate("/signin")} data-testid="invite-signin"
          className="bg-[#C8A97E] text-[#0D0D0D] px-8 py-3.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">Sign In</button>
        <button onClick={() => navigate("/signup")} data-testid="invite-signup"
          className="border border-[#F5F2EA]/20 px-8 py-3.5 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">Create Account</button>
      </div>
    </div>
  );
}

function EmptyStart({ onAdd }) {
  return (
    <div className="border border-[#F5F2EA]/10 bg-[#111111] mt-14 overflow-hidden md:grid md:grid-cols-2">
      <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-[#141414]">
        <img src={SCENE.wardrobe} alt="A minimalist styled wardrobe rail" loading="lazy" className="w-full h-full object-cover" />
      </div>
      <div className="p-12 md:p-16 text-center flex flex-col items-center justify-center">
        <MoireSymbol size={40} />
        <h2 className="font-serif text-3xl text-[#F5F2EA] mt-6">Start your wardrobe</h2>
        <p className="font-sans text-sm text-[#8A8170] mt-3">Photograph your clothes once. Let MOIRÉ remember them for you.</p>
        <button onClick={onAdd} data-testid="add-first-piece"
          className="mt-8 inline-flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-8 py-3.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">
          <Plus size={16} strokeWidth={2} /> Add Your First Piece
        </button>
      </div>
    </div>
  );
}

function EmptyCategory({ group, onAdd }) {
  const label = group === "shoes" ? "shoes" : group === "all" ? "pieces" : group;
  return (
    <div className="border border-[#F5F2EA]/10 p-14 text-center mt-8">
      <p className="font-serif text-2xl text-[#F5F2EA]">Nothing here yet.</p>
      <p className="font-sans text-sm text-[#8A8170] mt-2">Add a piece and MOIRÉ can start building looks around your {label}.</p>
      <button onClick={onAdd} className="mt-6 border border-[#C8A97E] text-[#C8A97E] px-6 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors">Add a Piece</button>
    </div>
  );
}

function StatsBar({ stats }) {
  return (
    <div className="border border-[#F5F2EA]/10 bg-[#111111] p-6 mt-10 flex flex-wrap items-center gap-x-10 gap-y-4">
      <div>
        <div className="font-serif text-4xl text-[#C8A97E]">{stats.total}</div>
        <div className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">pieces</div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        {Object.entries(stats.groups).map(([g, n]) => (
          <span key={g} className="font-sans text-sm text-[#F5F2EA]/70 capitalize">{n} {g}</span>
        ))}
      </div>
      {stats.colours?.length > 0 && (
        <div className="flex items-center gap-3 ml-auto">
          <span className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Your colours</span>
          <div className="flex gap-1.5">
            {stats.colours.map((c) => <span key={c.name} title={c.name} className="h-6 w-6 border border-[#F5F2EA]/15" style={{ backgroundColor: c.hex }} />)}
          </div>
        </div>
      )}
      {stats.insights?.length > 0 && (
        <p className="w-full font-serif italic text-[#8A8170] border-t border-[#F5F2EA]/10 pt-3">{stats.insights[0]}</p>
      )}
    </div>
  );
}

function WardrobeTile({ item, onOpen, onChanged }) {
  const toggleFav = async (e) => {
    e.stopPropagation();
    try { await api.patch(`/wardrobe/${item.id}`, { favourite: !item.favourite }); onChanged(); } catch {}
  };
  return (
    <div onClick={onOpen} data-testid={`wtile-${item.id}`}
      className="group relative border border-[#F5F2EA]/10 bg-[#111111] cursor-pointer hover:border-[#C8A97E]/50 transition-colors overflow-hidden">
      <div className="aspect-[3/4] bg-[#1A1A1A] overflow-hidden">
        {item.image_url
          ? <img src={imageUrl(item.image_url)} alt={item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center"><Swatch hex={item.primary_color_hex} /></div>}
      </div>
      <button onClick={toggleFav} data-testid={`wfav-${item.id}`}
        className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center bg-[#0D0D0D]/70">
        <Heart size={15} strokeWidth={1.5} className="text-[#C8A97E]" fill={item.favourite ? "#C8A97E" : "none"} />
      </button>
      <div className="p-3">
        <div className="font-serif text-lg text-[#F5F2EA] truncate">{item.name}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className="h-3 w-3 border border-[#F5F2EA]/20" style={{ backgroundColor: item.primary_color_hex }} />
          <span className="font-sans text-[11px] text-[#8A8170] capitalize">{item.primary_color_name} · {item.category}</span>
        </div>
      </div>
    </div>
  );
}

function AddDialog({ open, onClose, onAdded }) {
  const [step, setStep] = useState("upload"); // upload | analyzing | confirm
  const [file, setFile] = useState(null);
  const { url: preview, setFromFile: setPreviewFile, clear: clearPreview } = useObjectUrl();
  const [analysis, setAnalysis] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [form, setForm] = useState({ custom_name: "", category: "", pattern: "solid", primary_color_name: "", primary_color_hex: "#8A8170", style_tags: [], favourite: false });

  const analyze = async (f) => {
    setStep("analyzing");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const a = data.analysis;
      setAnalysis(a); setItemId(data.item_id); setImgUrl(data.image_url);
      setForm({ custom_name: "", category: a.category || "", pattern: a.pattern || "solid",
        primary_color_name: a.primary_color_name, primary_color_hex: a.primary_color_hex,
        style_tags: a.style ? [a.style] : [], favourite: false });
      setStep("confirm");
    } catch { toast.error("Couldn't read that photo."); setStep("upload"); }
  };

  const save = async (addAnother) => {
    try {
      await api.post("/wardrobe", { analysis, item_id: itemId, image_path: imgUrl, ...form });
      toast.success("Added to your wardrobe.");
      onAdded();
      if (addAnother) { setStep("upload"); setFile(null); clearPreview(); setAnalysis(null); }
      else onClose();
    } catch { toast.error("Couldn't save that piece."); }
  };

  const toggleTag = (t) => setForm((f) => ({ ...f, style_tags: f.style_tags.includes(t) ? f.style_tags.filter(x => x !== t) : [...f.style_tags, t] }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0D0D0D] border border-[#C8A97E]/40 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-dialog">
        <DialogTitle className="font-serif text-2xl text-[#F5F2EA] font-normal">Add a piece</DialogTitle>
        <DialogDescription className="sr-only">Photograph or upload a garment to add to your wardrobe.</DialogDescription>
        {step === "upload" && (
          <div className="mt-2">
            <UploadZone testid="wadd" label="Snap your piece" preview={preview}
              onFile={async (f) => {
                try { const p = await prepareImage(f); setFile(p); setPreviewFile(p); analyze(p); }
                catch { toast.error(PHOTO_ERROR_MSG); }
              }}
              onClear={() => { setFile(null); clearPreview(); }} />
          </div>
        )}
        {step === "analyzing" && <AnalyzingSequence stages={["Looking at the piece…", "Reading the colour…", "Adding to your wardrobe…"]} />}
        {step === "confirm" && analysis && (
          <div className="mt-2 space-y-4">
            {imgUrl && <img src={imageUrl(imgUrl)} alt="piece" className="w-full h-44 object-cover border border-[#F5F2EA]/10" />}
            <div>
              <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Name (optional)</label>
              <Input value={form.custom_name} onChange={(e) => setForm({ ...form, custom_name: e.target.value })} data-testid="wadd-name"
                placeholder={`${form.primary_color_name} ${form.category}`} className="bg-transparent border-[#F5F2EA]/20 rounded-none text-[#F5F2EA] placeholder:text-[#8A8170]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Category</label>
                <Select value={CATEGORIES.find(c => c.toLowerCase() === (form.category || "").toLowerCase()) || form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="wadd-category" className="bg-transparent border-[#F5F2EA]/20 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#F5F2EA]/15 text-[#F5F2EA] max-h-56">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Pattern</label>
                <Select value={PATTERNS.find(p => p.toLowerCase() === form.pattern.toLowerCase()) || "Solid"} onValueChange={(v) => setForm({ ...form, pattern: v.toLowerCase() })}>
                  <SelectTrigger className="bg-transparent border-[#F5F2EA]/20 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#F5F2EA]/15 text-[#F5F2EA]">{PATTERNS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Colour</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={form.primary_color_hex} onChange={(e) => setForm({ ...form, primary_color_hex: e.target.value })} className="h-9 w-11 bg-transparent border border-[#F5F2EA]/20" />
                <Input value={form.primary_color_name} onChange={(e) => setForm({ ...form, primary_color_name: e.target.value })} className="bg-transparent border-[#F5F2EA]/20 rounded-none text-[#F5F2EA]" />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {COLOR_FAMILIES.map(f => <button key={f.name} title={f.name} onClick={() => setForm({ ...form, primary_color_hex: f.hex, primary_color_name: f.name })} className="h-6 w-6 border border-[#F5F2EA]/15 hover:border-[#C8A97E]" style={{ backgroundColor: f.hex }} />)}
              </div>
            </div>
            <div>
              <label className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170]">Style tags</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {STYLE_TAGS.map(t => (
                  <button key={t} onClick={() => toggleTag(t)}
                    className={`px-3 py-1.5 font-sans text-[10px] tracking-wider uppercase border ${form.style_tags.includes(t) ? "border-[#C8A97E] text-[#C8A97E]" : "border-[#F5F2EA]/15 text-[#8A8170]"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => save(false)} data-testid="wadd-save"
                className="flex-1 bg-[#C8A97E] text-[#0D0D0D] py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors">Add to Wardrobe</button>
              <button onClick={() => save(true)} data-testid="wadd-save-another"
                className="border border-[#F5F2EA]/20 px-4 py-3 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">Add Another</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ item, onClose, onChanged, navigate }) {
  const [matchCat, setMatchCat] = useState(matchOptionsFor(item.category)[0]);
  const [wardrobeMatches, setWardrobeMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const options = matchOptionsFor(item.category);

  const showColours = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/match", { analysis: toAnalysis(item), match_category: matchCat, personality: "classic" });
      saveResult({ ...data, image_url: imageUrl(item.image_url) });
      navigate("/results");
    } catch { toast.error("Couldn't style that just now."); } finally { setLoading(false); }
  };

  const useWardrobe = async () => {
    setLoading(true); setWardrobeMatches(null);
    try {
      const { data } = await api.post("/wardrobe/match", { source_analysis: toAnalysis(item), match_category: matchCat });
      setWardrobeMatches(data);
    } catch { toast.error("Couldn't search your wardrobe."); } finally { setLoading(false); }
  };

  const del = async () => {
    try { await api.delete(`/wardrobe/${item.id}`); toast.success("Removed."); onChanged(); onClose(); }
    catch { toast.error("Couldn't remove that piece."); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0D0D0D] border border-[#C8A97E]/40 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wdetail">
        <DialogTitle className="sr-only">{item.name}</DialogTitle>
        <DialogDescription className="sr-only">Wardrobe item detail and styling.</DialogDescription>
        <div className="grid md:grid-cols-2 gap-6">
          {item.image_url && <img src={imageUrl(item.image_url)} alt={item.name} className="w-full h-72 object-cover border border-[#F5F2EA]/10" />}
          <div>
            <h2 className="font-serif text-3xl text-[#F5F2EA]">{item.name}</h2>
            <div className="flex items-center gap-3 mt-3">
              <Swatch hex={item.primary_color_hex} size="md" />
              <div>
                <div className="font-sans text-sm text-[#F5F2EA]">{item.primary_color_name}</div>
                <div className="font-sans text-[11px] text-[#8A8170] capitalize">{item.category} · {item.pattern}{item.material ? ` · ${item.material}` : ""}</div>
              </div>
            </div>
            {item.style_tags?.length > 0 && <p className="font-sans text-[11px] tracking-wider uppercase text-[#8A8170] mt-3">{item.style_tags.join(" · ")}</p>}

            <div className="gold-line my-5" />
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-2">Style this — what are we matching?</p>
            <div className="flex flex-wrap gap-2">
              {options.map(o => (
                <button key={o} onClick={() => { setMatchCat(o); setWardrobeMatches(null); }} data-testid={`wmatchcat-${o.replace(/[^a-z]/gi,"").toLowerCase()}`}
                  className={`px-3 py-1.5 font-sans text-[10px] tracking-wider uppercase border ${matchCat === o ? "border-[#C8A97E] bg-[#C8A97E] text-[#0D0D0D]" : "border-[#F5F2EA]/15 text-[#F5F2EA]/70 hover:border-[#C8A97E]"}`}>{o}</button>
              ))}
            </div>
            <div className="flex flex-col gap-2.5 mt-5">
              <button onClick={showColours} disabled={loading} data-testid="style-show-colours"
                className="flex items-center justify-center gap-2 bg-[#C8A97E] text-[#0D0D0D] py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors disabled:opacity-50">
                <Sparkles size={14} strokeWidth={1.5} /> Show Me Colours</button>
              <button onClick={useWardrobe} disabled={loading} data-testid="style-use-wardrobe"
                className="flex items-center justify-center gap-2 border border-[#C8A97E] text-[#C8A97E] py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors disabled:opacity-50">
                Use My Wardrobe</button>
              <button onClick={() => { sessionStorage.setItem("moire.lookRequest", JSON.stringify({ source_wardrobe_id: item.id, occasion: null })); navigate("/look"); }} data-testid="style-see-look"
                className="flex items-center justify-center gap-2 border border-[#F5F2EA]/15 text-[#F5F2EA]/80 py-3 font-sans text-xs tracking-luxe uppercase hover:border-[#C8A97E] transition-colors">
                See The Full Look</button>
            </div>
          </div>
        </div>

        {wardrobeMatches && (
          <div className="mt-6 border-t border-[#F5F2EA]/10 pt-5" data-testid="wardrobe-matches">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-3">From your wardrobe</p>
            {wardrobeMatches.length === 0 ? (
              <p className="font-sans text-sm text-[#8A8170]">You don't own a {matchCat.toLowerCase()} yet. Add one and MOIRÉ will style it.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {wardrobeMatches.map((m, i) => (
                  <div key={m.item.id} className="flex items-center gap-3 border border-[#F5F2EA]/10 p-3">
                    <span className="font-sans text-[10px] text-[#8A8170]">{String(i + 1).padStart(2, "0")}</span>
                    {m.item.image_url
                      ? <img src={imageUrl(m.item.image_url)} alt={m.item.name} loading="lazy" className="h-12 w-12 object-cover border border-[#F5F2EA]/15" />
                      : <span className="h-12 w-12 border border-[#F5F2EA]/15" style={{ backgroundColor: m.item.primary_color_hex }} />}
                    <span className="font-serif text-lg text-[#F5F2EA] flex-1 truncate">{m.item.name}</span>
                    <span className="font-serif text-xl text-[#C8A97E]">{Number(m.score).toFixed(1)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-[#F5F2EA]/10">
          {confirmDel ? (
            <>
              <span className="font-sans text-xs text-[#8A8170]">Delete this piece?</span>
              <button onClick={del} data-testid="wdelete-confirm" className="text-xs tracking-luxe uppercase text-[#0D0D0D] bg-[#C8A97E] px-4 py-2">Yes, delete</button>
              <button onClick={() => setConfirmDel(false)} className="text-xs tracking-luxe uppercase text-[#8A8170]">Cancel</button>
            </>
          ) : (
            <button onClick={() => setConfirmDel(true)} data-testid="wdelete"
              className="flex items-center gap-2 font-sans text-xs tracking-luxe uppercase text-[#8A8170] hover:text-[#F5F2EA]">
              <Trash2 size={14} strokeWidth={1.5} /> Delete</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
